<template>
    <BaseDialog v-model:open="open" :title="heading">
        <div class="flex flex-col gap-4" data-testid="sharing-intro-dialog">
            <div class="flex items-center gap-3 rounded-lgr bg-surface-sunken p-4">
                <span class="grid size-11 shrink-0 place-items-center rounded-xl bg-surface-raised text-ink-muted">
                    <i class="bi text-xl/none" :class="calloutIcon" aria-hidden="true"></i>
                </span>
                <p class="body-small text-ink-muted min-w-0">{{ calloutText }}{{ ` ${sharingDisclosure}` }}</p>
            </div>

            <BaseNotification v-if="teamsEnabled" theme="info" hide-dismiss :text="t('collaborationProfile.sharing.teamAccess')" />

            <p class="body text-ink-muted">
                To change who can see it, use the <span class="body-bold text-ink">{{ shareButtonText }}</span> button at the top right of your profile.
                <template v-if="alternativeAccessCopy">You can {{ alternativeAccessCopy }} at any time.</template>
            </p>
        </div>
        <template #footer><BaseButton theme="primary" @click="open = false">Got it</BaseButton></template>
    </BaseDialog>
</template>

<script setup lang="ts">
import { useProfileSharingCopy } from "~vue/CollaborationProfile/composables/useProfileSharingCopy";
import BaseButton from "~vue/components/BaseButton.vue";
import BaseDialog from "~vue/components/BaseDialog.vue";
import BaseNotification from "~vue/components/BaseNotification.vue";
import { ACCESS, ACCESS_BUTTON_LABELS } from "~vue/utils/constants";
import { computed } from "vue";
import { useI18n } from "vue-i18n";

const { t } = useI18n();

const { sharingDisclosure, teamsEnabled } = useProfileSharingCopy();

const { access, availableAccessLevels } = defineProps<{ access: ACCESS; availableAccessLevels: Set<string> }>();

const open = defineModel<boolean>("open", { default: false });

const HEADINGS: Record<ACCESS, string> = {
    [ACCESS.ORGANIZATION]: "Your Collaboration Profile is discoverable by your organization",
    [ACCESS.INVITED_ORG_MEMBERS]: "Your Collaboration Profile is currently visible to colleagues you invite",
    [ACCESS.PRIVATE]: "Your Collaboration Profile is currently private",
};

const calloutText = computed(
    () =>
        ({
            [ACCESS.ORGANIZATION]: "New Collaboration Profiles are discoverable by default, so colleagues in your organization can find your profile and learn how you work best.",
            [ACCESS.INVITED_ORG_MEMBERS]: t("collaborationProfile.sharing.initialInvited"),
            [ACCESS.PRIVATE]: t("collaborationProfile.sharing.initialPrivate"),
        })[access],
);

const CALLOUT_ICONS: Record<ACCESS, string> = {
    [ACCESS.ORGANIZATION]: "bi-people-fill",
    [ACCESS.INVITED_ORG_MEMBERS]: "bi-person-plus-fill",
    [ACCESS.PRIVATE]: "bi-lock-fill",
};

const ALTERNATIVE_COPY: [ACCESS, string][] = [
    [ACCESS.INVITED_ORG_MEMBERS, "switch to invite-only"],
    [ACCESS.ORGANIZATION, "make your profile discoverable to your organization"],
    [ACCESS.PRIVATE, "make it private"],
];

const heading = computed(() => HEADINGS[access]);
const calloutIcon = computed(() => CALLOUT_ICONS[access]);
const shareButtonText = computed(() => ACCESS_BUTTON_LABELS[access]);

const alternativeAccessCopy = computed(() =>
    ALTERNATIVE_COPY.filter(([level]) => level !== access && availableAccessLevels.has(level))
        .map(([, copy]) => copy)
        .join(" or "),
);
</script>
